/**
 * Typed read/write helpers for the change-order-4.0 contracts (identities, wardrobe, three-stream claims, run
 * funding). Two layers:
 *  - `calls.*` build plain viem contract requests ({address, abi, functionName, args}); nothing is sent. Use them
 *    with `simulateContract` / `writeContract` / a wallet connector of your choice.
 *  - the async helpers (`claim`, `mintIdentity`, …) simulate first (so a revert surfaces before signing), send with
 *    the given wallet, wait for the receipt and return the decoded result.
 * Contracts compute every amount and beneficiary; these helpers never choose them.
 */
import { decodeEventLog, type Abi, type Account, type Address, type Chain, type Hash, type Hex, type PublicClient, type TransactionReceipt, type Transport, type WalletClient } from 'viem';
import { baseTraits, runReservation, traitLabels, type BaseTraits } from '@runner/simulation';
import {
  encounterRegistryAbi,
  runnerGameAbi,
  runnerIdentityAbi,
  runnerRewardsAbi,
  runnerTokenAbi,
  runnerTreasuryAbi,
  runnerWardrobeAbi,
} from './abis.js';
import type { RunnerDeployment } from './index.js';

type D = Pick<RunnerDeployment, 'rewards' | 'identity' | 'wardrobe' | 'runnerToken' | 'treasury' | 'game' | 'registry'>;

export interface ContractCall {
  address: Address;
  abi: Abi;
  functionName: string;
  args: readonly unknown[];
}

const call = (address: Address, abi: unknown, functionName: string, args: readonly unknown[] = []): ContractCall => ({ address, abi: abi as Abi, functionName, args });

/** Request builders (nothing is sent). */
export const calls = {
  approveVault: (d: D, amount: bigint) => call(d.runnerToken, runnerTokenAbi, 'approve', [d.rewards, amount]),
  deposit: (d: D, amount: bigint) => call(d.rewards, runnerRewardsAbi, 'deposit', [amount]),
  withdraw: (d: D, amount: bigint) => call(d.rewards, runnerRewardsAbi, 'withdraw', [amount]),
  setPayout: (d: D, payout: Address) => call(d.rewards, runnerRewardsAbi, 'setPayout', [payout]),
  /** Anyone may submit; the contract pays the account's recorded payout address. Returns (direct, deferred, run). */
  claim: (d: D, account: Address, maxSegments = 64n) => call(d.rewards, runnerRewardsAbi, 'claim', [account, maxSegments]),
  withdrawUnpaid: (d: D, to: Address) => call(d.rewards, runnerRewardsAbi, 'withdrawUnpaid', [to]),
  /** Deposit exactly B RUNNER (approve the vault first) and receive a new Runner. Eligible from the next epoch. */
  mintIdentity: (d: D) => call(d.rewards, runnerRewardsAbi, 'mintIdentity'),
  /** Turn B of the caller's ordinary position into a Runner (maturity, weight and claims unchanged). */
  convertToIdentity: (d: D) => call(d.rewards, runnerRewardsAbi, 'convertToIdentity'),
  /** Owner only: burn the Runner and keep its backing as an ordinary position. */
  convertToOrdinary: (d: D, tokenId: bigint) => call(d.rewards, runnerRewardsAbi, 'convertToOrdinary', [tokenId]),
  /** Owner only (approved operators cannot): burn the Runner and receive its backing B in RUNNER. */
  redeemIdentity: (d: D, tokenId: bigint) => call(d.rewards, runnerRewardsAbi, 'redeemIdentity', [tokenId]),
  /** ERC-721 transfer: moves backing and future rights; the seller keeps every right recorded before this epoch. */
  transferIdentity: (d: D, from: Address, to: Address, tokenId: bigint) => call(d.identity, runnerIdentityAbi, 'transferFrom', [from, to, tokenId]),
  safeTransferIdentity: (d: D, from: Address, to: Address, tokenId: bigint) => call(d.identity, runnerIdentityAbi, 'safeTransferFrom', [from, to, tokenId]),
  /** Owner only. Equipment is keyed by (token, owner): a new owner starts with the base look. itemId 0 unequips. */
  equip: (d: D, tokenId: bigint, slot: number, itemId: number) => call(d.wardrobe, runnerWardrobeAbi, 'equip', [tokenId, slot, itemId]),
} as const;

export type Wallet = WalletClient<Transport, Chain, Account>;

export class CallReverted extends Error {
  constructor(
    readonly functionName: string,
    readonly hash: Hash,
  ) {
    super(`${functionName} reverted in ${hash}`);
  }
}

/** Simulate, send and wait. Returns the simulated return value and the receipt. */
export async function send<T = unknown>(pc: PublicClient, wallet: Wallet, c: ContractCall): Promise<{ result: T; hash: Hash; receipt: TransactionReceipt }> {
  const sim = await pc.simulateContract({ ...c, account: wallet.account, chain: wallet.chain } as never);
  const hash = await wallet.writeContract((sim as { request: unknown }).request as never);
  const receipt = await pc.waitForTransactionReceipt({ hash });
  if (receipt.status !== 'success') throw new CallReverted(c.functionName, hash);
  return { result: (sim as { result: T }).result, hash, receipt };
}

function logsOf(receipt: TransactionReceipt, address: Address, abi: Abi) {
  const out: { eventName: string; args: Record<string, unknown> }[] = [];
  for (const l of receipt.logs) {
    if (l.address.toLowerCase() !== address.toLowerCase()) continue;
    try {
      out.push(decodeEventLog({ abi, data: l.data, topics: l.topics as [Hex, ...Hex[]] }) as never);
    } catch {
      /* not this ABI */
    }
  }
  return out;
}

export interface ClaimResult {
  direct: bigint;
  deferred: bigint;
  run: bigint;
  /** Where the contract sent the funds (null when the quote asset refused and the total was recorded as unpaid). */
  paidTo: Address | null;
  hash: Hash;
}

/** Claim the three streams (direct, deferred, run cohort) for `account`; funds go to its recorded payout address. */
export async function claim(pc: PublicClient, wallet: Wallet, d: D, account: Address, maxSegments = 64n): Promise<ClaimResult> {
  const { result, hash, receipt } = await send<readonly [bigint, bigint, bigint]>(pc, wallet, calls.claim(d, account, maxSegments));
  const ev = logsOf(receipt, d.rewards, runnerRewardsAbi as Abi).find((e) => e.eventName === 'Claimed');
  return { direct: result[0], deferred: result[1], run: result[2], paidTo: ev ? (ev.args.to as Address) : null, hash };
}

/** Mint a Runner by depositing exactly B (approves the vault for B first when the allowance is short). */
export async function mintIdentity(pc: PublicClient, wallet: Wallet, d: D): Promise<{ tokenId: bigint; backing: bigint; eligibleFrom: number; hash: Hash }> {
  const owner = wallet.account.address;
  const backing = (await pc.readContract({ address: d.rewards, abi: runnerRewardsAbi, functionName: 'identityBacking' })) as bigint;
  const allowance = (await pc.readContract({ address: d.runnerToken, abi: runnerTokenAbi, functionName: 'allowance', args: [owner, d.rewards] })) as bigint;
  if (allowance < backing) await send(pc, wallet, calls.approveVault(d, backing));
  return identityMinted(d, await send<bigint>(pc, wallet, calls.mintIdentity(d)));
}

export async function convertToIdentity(pc: PublicClient, wallet: Wallet, d: D): Promise<{ tokenId: bigint; backing: bigint; eligibleFrom: number; hash: Hash }> {
  return identityMinted(d, await send<bigint>(pc, wallet, calls.convertToIdentity(d)));
}

function identityMinted(d: D, r: { result: bigint; hash: Hash; receipt: TransactionReceipt }) {
  const ev = logsOf(r.receipt, d.rewards, runnerRewardsAbi as Abi).find((e) => e.eventName === 'IdentityMinted');
  return { tokenId: r.result, backing: (ev?.args.backing as bigint) ?? 0n, eligibleFrom: Number(ev?.args.eligibleFrom ?? 0), hash: r.hash };
}

export async function convertToOrdinary(pc: PublicClient, wallet: Wallet, d: D, tokenId: bigint): Promise<{ hash: Hash }> {
  return { hash: (await send(pc, wallet, calls.convertToOrdinary(d, tokenId))).hash };
}

export async function redeemIdentity(pc: PublicClient, wallet: Wallet, d: D, tokenId: bigint): Promise<{ backing: bigint; hash: Hash }> {
  const r = await send(pc, wallet, calls.redeemIdentity(d, tokenId));
  const ev = logsOf(r.receipt, d.rewards, runnerRewardsAbi as Abi).find((e) => e.eventName === 'IdentityClosed');
  return { backing: (ev?.args.backing as bigint) ?? 0n, hash: r.hash };
}

/** Transfer a Runner (from = the wallet unless an approved operator transfers on the owner's behalf). */
export async function transferIdentity(pc: PublicClient, wallet: Wallet, d: D, to: Address, tokenId: bigint, from: Address = wallet.account.address, safe = false): Promise<{ epoch: number; sellerWasEligible: boolean; hash: Hash }> {
  const r = await send(pc, wallet, safe ? calls.safeTransferIdentity(d, from, to, tokenId) : calls.transferIdentity(d, from, to, tokenId));
  const ev = logsOf(r.receipt, d.rewards, runnerRewardsAbi as Abi).find((e) => e.eventName === 'IdentityTransferred');
  return { epoch: Number(ev?.args.epoch ?? 0), sellerWasEligible: Boolean(ev?.args.sellerWasEligible), hash: r.hash };
}

export async function equip(pc: PublicClient, wallet: Wallet, d: D, tokenId: bigint, slot: number, itemId: number): Promise<{ hash: Hash }> {
  return { hash: (await send(pc, wallet, calls.equip(d, tokenId, slot, itemId))).hash };
}

// ---------------------------------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------------------------------

type ReadOpts = { blockNumber?: bigint };
const rd = <T>(pc: PublicClient, address: Address, abi: unknown, functionName: string, args: readonly unknown[] = [], o: ReadOpts = {}) =>
  pc.readContract({ address, abi: abi as Abi, functionName, args, ...(o.blockNumber !== undefined ? { blockNumber: o.blockNumber } : {}) } as never) as Promise<T>;

export async function readClaimable(pc: PublicClient, d: D, account: Address, maxSegments = 64n, o: ReadOpts = {}): Promise<{ direct: bigint; deferred: bigint; run: bigint; total: bigint }> {
  const [direct, deferred, run] = await rd<readonly [bigint, bigint, bigint]>(pc, d.rewards, runnerRewardsAbi, 'claimable', [account, maxSegments], o);
  return { direct, deferred, run, total: direct + deferred + run };
}

export interface PrincipalSplit {
  eligible: bigint;
  pending: bigint;
  total: bigint;
}

/**
 * One ledger: the account's principal (ordinary + identity-backed) and the identity-backed PART of it. Never add the
 * two: `identity` is included in `total`; `ordinary = total − identity`.
 */
export async function readPrincipal(pc: PublicClient, d: D, account: Address, o: ReadOpts = {}): Promise<{ total: PrincipalSplit; identity: PrincipalSplit; ordinary: PrincipalSplit }> {
  const [[e, p], [ie, ip]] = await Promise.all([
    rd<readonly [bigint, bigint]>(pc, d.rewards, runnerRewardsAbi, 'depositOf', [account], o),
    rd<readonly [bigint, bigint]>(pc, d.rewards, runnerRewardsAbi, 'identityPrincipalOf', [account], o),
  ]);
  const split = (a: bigint, b: bigint): PrincipalSplit => ({ eligible: a, pending: b, total: a + b });
  return { total: split(e, p), identity: split(ie, ip), ordinary: split(e - ie, p - ip) };
}

export interface IdentityView {
  tokenId: bigint;
  live: boolean;
  owner: Address | null;
  backing: bigint;
  eligibleFrom: number;
  mintedEpoch: number;
  traits: BaseTraits;
  traitLabels: ReturnType<typeof traitLabels>;
  /** Equipment of the CURRENT owner (wardrobe.equipment); empty slots are 0. */
  equipment: readonly number[];
}

export async function readIdentity(pc: PublicClient, d: D, tokenId: bigint, o: ReadOpts = {}): Promise<IdentityView> {
  const [info, seed] = await Promise.all([
    rd<{ live: boolean; backing: bigint; eligibleFrom: number; mintedEpoch: number }>(pc, d.rewards, runnerRewardsAbi, 'identityInfo', [tokenId], o),
    rd<Hex>(pc, d.identity, runnerIdentityAbi, 'traitSeed', [], o),
  ]);
  const owner = info.live ? await rd<Address>(pc, d.identity, runnerIdentityAbi, 'ownerOf', [tokenId], o) : null;
  const equipment = info.live ? await rd<readonly number[]>(pc, d.wardrobe, runnerWardrobeAbi, 'equipment', [tokenId], o) : [0, 0, 0, 0];
  const traits = baseTraits(seed, tokenId);
  return {
    tokenId,
    live: info.live,
    owner,
    backing: info.backing,
    eligibleFrom: Number(info.eligibleFrom),
    mintedEpoch: Number(info.mintedEpoch),
    traits,
    traitLabels: traitLabels(traits),
    equipment: equipment.map(Number),
  };
}

export type FundingReason = 'fundable' | 'no-active-config' | 'funding-paused' | 'road-reserve-below-minimum' | 'operations-below-provision';

export interface RunFunding {
  fundable: boolean;
  /**
   * Not fundable, but the active configuration has minRunBudget == 0: requestCourse would start an EXHIBITION run
   * (budget 0, no road budget at stake; D48) instead of reverting NotFunded. `reason` still says why it is unfunded.
   */
  exhibition: boolean;
  reason: FundingReason;
  /** What requestCourse would reserve now (0 when not fundable). Mirrors RunnerTreasury.reserveRun. */
  budget: bigint;
  configVersion: number;
  roadReserve: bigint;
  operations: bigint;
  runBudget: bigint;
  minRunBudget: bigint;
  opsProvision: bigint;
  fundingPaused: boolean;
}

/**
 * Would `requestCourse` fund a run right now (or start an exhibition run)? Mirrors RunnerTreasury.reserveRun against
 * the treasury views and the active configuration's road parameters (simulation `runReservation`). Settlement inside
 * requestCourse may add newly closed epochs' road share first, so this is a lower bound at the read block.
 */
export async function readRunFunding(pc: PublicClient, d: D, o: ReadOpts = {}): Promise<RunFunding> {
  const [v, roadReserve, operations, fundingPaused] = await Promise.all([
    rd<number>(pc, d.registry, encounterRegistryAbi, 'activeConfigVersion', [], o),
    rd<bigint>(pc, d.treasury, runnerTreasuryAbi, 'roadReserve', [], o),
    rd<bigint>(pc, d.treasury, runnerTreasuryAbi, 'operations', [], o),
    rd<boolean>(pc, d.game, runnerGameAbi, 'fundingPaused', [], o),
  ]);
  const configVersion = Number(v);
  const road = configVersion > 0 ? await rd<{ runBudget: bigint; minRunBudget: bigint; opsProvision: bigint }>(pc, d.registry, encounterRegistryAbi, 'roadParams', [configVersion], o) : { runBudget: 0n, minRunBudget: 0n, opsProvision: 0n };
  const res = configVersion > 0 && !fundingPaused ? runReservation(roadReserve, operations, road.runBudget, road.minRunBudget, road.opsProvision) : null;
  const budget = res?.budget ?? 0n;
  const reason: FundingReason =
    configVersion === 0
      ? 'no-active-config'
      : fundingPaused
        ? 'funding-paused'
        : budget > 0n
          ? 'fundable'
          : operations < road.opsProvision
            ? 'operations-below-provision'
            : 'road-reserve-below-minimum';
  return { fundable: budget > 0n, exhibition: res?.exhibition ?? false, reason, budget, configVersion, roadReserve, operations, runBudget: road.runBudget, minRunBudget: road.minRunBudget, opsProvision: road.opsProvision, fundingPaused };
}

export interface RunEscrowView {
  open: boolean;
  cohortEpoch: number;
  policyId: number;
  budget: bigint;
  assigned: bigint;
  toHolders: bigint;
  toForge: bigint;
  provision: bigint;
  unassigned: bigint;
}

export async function readRunEscrow(pc: PublicClient, d: D, runId: number, o: ReadOpts = {}): Promise<RunEscrowView> {
  const e = await rd<{ open: boolean; cohortEpoch: number; policyId: number; budget: bigint; assigned: bigint; toHolders: bigint; toForge: bigint; provision: bigint }>(pc, d.treasury, runnerTreasuryAbi, 'runEscrow', [runId], o);
  return { ...e, cohortEpoch: Number(e.cohortEpoch), policyId: Number(e.policyId), unassigned: e.open ? e.budget - e.assigned : 0n };
}
